import "@/App.css";
import "./components/sections.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import React, { useEffect, lazy, Suspense } from 'react';
import { AlertTriangle, Flame, ShieldOff, Zap, Trophy, LayoutGrid, ArrowRightLeft, MessageCircleQuestion, Rocket } from 'lucide-react';
import Header from "./components/Header";
import Hero from "./components/Hero";
import Footer from "./components/Footer";
import ScrollProgressBar from "./components/ScrollProgressBar";
import StickyBottomCTA from "./components/StickyBottomCTA";
import QualifyChat from "./components/QualifyChat";
import AIAgentFocusSection from "./components/AIAgentFocusSection";
import useScrollPerformance from "./hooks/useScrollPerformance";

// Lazy load heavy components (inkl. Spline 3D)
const SplineSection = lazy(() => import("./components/SplineSection"));
const SplineSectionTypography = lazy(() => import("./components/SplineSectionTypography"));
const VideoSection = lazy(() => import("./components/VideoSection"));
const KISystemSection = lazy(() => import("./components/KISystemSection"));
const LeadLossSection = lazy(() => import("./components/LeadLossSection"));
const ChallengesSection = lazy(() => import("./components/ChallengesSection"));
const SectionDivider = lazy(() => import("./components/SectionDivider"));
const WhyNothingWorkedSection = lazy(() => import("./components/WhyNothingWorkedSection"));
const ConceptSection = lazy(() => import("./components/ConceptSection"));
const FeaturesHighlightSection = lazy(() => import("./components/FeaturesHighlightSection"));
const ProblemSection = lazy(() => import("./components/ProblemSection"));
const FeaturesSection = lazy(() => import("./components/FeaturesSection"));
const WhatsAppShowcase = lazy(() => import("./components/WhatsAppShowcase"));
const StatsSection = lazy(() => import("./components/StatsSection"));
const TransformationSection = lazy(() => import("./components/TransformationSection"));
const CRMPreview = lazy(() => import("./components/CRMPreview"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const TestimonialsSection = lazy(() => import("./components/TestimonialsSection"));
const ContactForm = lazy(() => import("./components/ContactForm"));
const ContactSubmissionsAdmin = lazy(() => import("./components/ContactSubmissionsAdmin"));
const CTASection = lazy(() => import("./components/CTASection"));
const ValueStackSection = lazy(() => import("./components/ValueStackSection"));
const Impressum = lazy(() => import("./components/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const Leistungen = lazy(() => import("./pages/Leistungen"));
const KIAcademy = lazy(() => import("./pages/KIAcademy"));
const AcademyUsersAdmin = lazy(() => import("./pages/AcademyUsersAdmin"));
const Tools = lazy(() => import("./pages/Tools"));
const LeadHandout = lazy(() => import("./pages/LeadHandout"));
const LeaMath = lazy(() => import("./pages/Lea/LeaMath"));

// Loading components with correct heights to prevent layout shift
const SplineLoadingFallback = () => (
  <div style={{
    minHeight: '600px',
    height: '600px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#000'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid rgba(0, 255, 209, 0.1)',
      borderTop: '3px solid #00FFD1',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
  </div>
);

const SplineTypographyLoadingFallback = () => (
  <div style={{
    minHeight: '940px',
    height: '940px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#000'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid rgba(0, 255, 209, 0.1)',
      borderTop: '3px solid #00FFD1',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
  </div>
);

const StandardLoadingFallback = () => (
  <div style={{
    minHeight: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#000'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid rgba(0, 255, 209, 0.1)',
      borderTop: '3px solid #00FFD1',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
  </div>
);

const Home = () => {
  useEffect(() => {
    // Prevent auto-scrolling from iframes and other sources
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Disable smooth scroll behavior that might cause auto-scrolling
    document.documentElement.style.scrollBehavior = 'auto';
    
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  return (
    <div className="dark-container">
      <ScrollProgressBar />
      <Header />
      
      <Suspense fallback={<SplineLoadingFallback />}>
        {/* SPLINE 3D ROBOT — auf Wunsch entfernt (Performance) */}
      </Suspense>
      
      {/* 1. HERO - Starkes Ergebnis + CTA mit RoboticHand */}
      <Hero />

      {/* 1b. AI AGENT FOCUS — Maximum spotlight direkt unter Hero */}
      <AIAgentFocusSection />
      
      <Suspense fallback={null}>
        <SectionDivider number="01" label="Die Herausforderungen" icon={<AlertTriangle size={14} strokeWidth={2.4} />} accent="#00D4FF" target="challenges" />
      </Suspense>
      
      {/* 2a. DIE DREI HERAUSFORDERUNGEN → ZEIT (Neue prominente Sektion) */}
      <Suspense fallback={<StandardLoadingFallback />}>
        <ChallengesSection />
      </Suspense>
      
      <Suspense fallback={null}>
        <SectionDivider number="02" label="Das echte Problem" icon={<Flame size={14} strokeWidth={2.4} />} accent="#F97316" target="lead-loss" />
      </Suspense>
      
      {/* 2b. PROBLEM / SCHMERZ - Nach oben gezogen */}
      <Suspense fallback={<StandardLoadingFallback />}>
        <LeadLossSection />
      </Suspense>
      
      <Suspense fallback={null}>
        <SectionDivider number="03" label="Warum nichts funktioniert" icon={<ShieldOff size={14} strokeWidth={2.4} />} accent="#EC4899" target="why-nothing" />
      </Suspense>
      
      {/* 3. WARUM BISHER NICHTS FUNKTIONIERT HAT - NEU */}
      <Suspense fallback={<StandardLoadingFallback />}>
        <WhyNothingWorkedSection />
      </Suspense>
      
      <Suspense fallback={null}>
        <SectionDivider number="04" label="Die Lösung" icon={<Zap size={14} strokeWidth={2.4} />} accent="#00FFD1" target="masterclass" />
      </Suspense>
      
      {/* 4. DIE LÖSUNG - KI-System + Konzept + Video */}
      <Suspense fallback={<StandardLoadingFallback />}>
        <KISystemSection />
        <ConceptSection />
        <VideoSection />
      </Suspense>
      
      <Suspense fallback={null}>
        <SectionDivider number="05" label="Ergebnisse aus der Praxis" icon={<Trophy size={14} strokeWidth={2.4} />} accent="#A855F7" target="results" />
      </Suspense>
      
      {/* 5. SOCIAL PROOF - Früher platziert */}
      <Suspense fallback={<StandardLoadingFallback />}>
        <TestimonialsSection />
      </Suspense>
      
      <Suspense fallback={null}>
        <SectionDivider number="06" label="Das komplette System" icon={<LayoutGrid size={14} strokeWidth={2.4} />} accent="#00D4FF" target="features" />
      </Suspense>
      
      {/* 6. INHALTE & MODULE - Features + Tools */}
      <Suspense fallback={<StandardLoadingFallback />}>
        <FeaturesHighlightSection />
        <WhatsAppShowcase />
        <CRMPreview />
      </Suspense>
      
      <Suspense fallback={null}>
        <SectionDivider number="07" label="Vorher · Nachher" icon={<ArrowRightLeft size={14} strokeWidth={2.4} />} accent="#F97316" target="transformation" />
      </Suspense>
      
      {/* 7. TRANSFORMATION - Vorher/Nachher */}
      <Suspense fallback={<StandardLoadingFallback />}>
        <TransformationSection />
      </Suspense>
      
      <Suspense fallback={null}>
        <SectionDivider number="08" label="Häufige Fragen" icon={<MessageCircleQuestion size={14} strokeWidth={2.4} />} accent="#00FFD1" target="faq" />
      </Suspense>
      
      {/* 8. EINWÄNDE / FAQ - Optimiert */}
      <Suspense fallback={<StandardLoadingFallback />}>
        <FAQSection />
      </Suspense>
      
      <Suspense fallback={null}>
        <SectionDivider number="09" label="Jetzt starten" icon={<Rocket size={14} strokeWidth={2.4} />} accent="#00FFD1" target="cta" />
      </Suspense>
      
      {/* 9. FINALER CTA + Contact Form */}
      <Suspense fallback={<StandardLoadingFallback />}>
        <ContactForm />
      </Suspense>
      
      <Footer />

      <StickyBottomCTA />
      <QualifyChat />

      {/* Minimal CSS - nur notwendige Styles */}
      <style>{`
        /* Loading spinner animation */
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const protectionStyles = {
  warningOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100000,
    animation: 'fadeIn 0.3s ease',
  },
  warningBox: {
    background: 'rgba(255, 59, 48, 0.1)',
    border: '2px solid #ff3b30',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '500px',
    textAlign: 'center',
  },
  warningTitle: {
    color: '#ff3b30',
    fontSize: '24px',
    fontWeight: 'bold',
    marginTop: '20px',
    marginBottom: '15px',
  },
  warningText: {
    color: '#fff',
    fontSize: '16px',
    lineHeight: '1.6',
  },
  watermarkOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    zIndex: 9999,
    overflow: 'hidden',
  },
  watermark: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(-45deg)',
    fontSize: '80px',
    fontWeight: 'bold',
    color: 'rgba(0, 255, 209, 0.02)',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    pointerEvents: 'none',
  },
};

function App() {
  // Global scroll-performance optimization: pauses heavy animations while scrolling
  useScrollPerformance();

  // Capture referral code from URL (?ref=MPV-XXXXXX) once on load and stash it
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref) localStorage.setItem('mpv_ref_code', ref.toUpperCase());
    } catch { /* ignore */ }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/leistungen" element={
            <Suspense fallback={<StandardLoadingFallback />}>
              <Leistungen />
            </Suspense>
          } />
          <Route path="/ki-academy" element={
            <Suspense fallback={<StandardLoadingFallback />}>
              <KIAcademy />
            </Suspense>
          } />
          <Route path="/impressum" element={
            <Suspense fallback={<StandardLoadingFallback />}>
              <Impressum />
            </Suspense>
          } />
          <Route path="/datenschutz" element={
            <Suspense fallback={<StandardLoadingFallback />}>
              <Datenschutz />
            </Suspense>
          } />
          <Route path="/admin/kontakte" element={
            <Suspense fallback={<StandardLoadingFallback />}>
              <ContactSubmissionsAdmin />
            </Suspense>
          } />
          <Route path="/admin/academy-users" element={
            <Suspense fallback={<StandardLoadingFallback />}>
              <AcademyUsersAdmin />
            </Suspense>
          } />
          <Route path="/tools" element={
            <Suspense fallback={<StandardLoadingFallback />}>
              <Tools />
            </Suspense>
          } />
          <Route path="/lead-handout" element={
            <Suspense fallback={<StandardLoadingFallback />}>
              <LeadHandout />
            </Suspense>
          } />
          <Route path="/lea" element={
            <Suspense fallback={<StandardLoadingFallback />}>
              <LeaMath />
            </Suspense>
          } />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
